import React, { useMemo, useState, useRef } from 'react';
import { ReactFlow, Background, BackgroundVariant, Handle, Position } from '@xyflow/react';
import Dagre from '@dagrejs/dagre';
import { Card } from '@/components/ui/card';
import { ShipWheel, ShieldCheck, BookOpen, Code, Puzzle, Loader2, Copy, Check, GitFork } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { formatInternalName } from '../../utils/formatInternalName';
import { apiClient } from '../../api/client';
import { SaveToLibraryButton } from './SaveToLibraryButton';
import '@xyflow/react/dist/style.css';

import awsLightLogo from '../../assets/icons/aws.svg';
import awsDarkLogo from '../../assets/icons/aws-dark.svg';
import slackColorIcon from '../../assets/icons/slack-color.png';
import pagerdutyIcon from '../../assets/icons/pagerduty-icon.svg';
import posthogIcon from '../../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../../assets/icons/posthog-light.png';
import vercelIcon from '../../assets/icons/vercel.svg';
import vercelLightIcon from '../../assets/icons/vercel-light.svg';
import railwayIcon from '../../assets/icons/railway.svg';
import flyioIcon from '../../assets/icons/flyio.svg';
import nebiusIcon from '../../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../../assets/icons/daytona.svg';
import supabaseIcon from '../../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../../assets/icons/planetscale-mono.svg';
import neonIcon from '../../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../../assets/icons/clickhouse.svg';
import jenkinsIcon from '../../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../../assets/icons/okta-mono.svg';
import databricksIcon from '../../assets/icons/databricks.png';
import { ReactComponent as GCPPreviewIcon } from '../../assets/icons/gcp.svg';
import terraformIcon from '../../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../../assets/icons/infisical.svg';
import confluenceIcon from '../../assets/icons/confluence.svg';
import jiraIcon from '../../assets/icons/jira.svg';
import linearIcon from '../../assets/icons/linear.png';
import linearDarkIcon from '../../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../../assets/icons/kestrel-dark.svg';
import argocdIcon from '../../assets/icons/argocd.svg';
import fluxcdIcon from '../../assets/icons/flux-cd.svg';
import karpenterIcon from '../../assets/icons/karpenter.svg';
import kyvernoIcon from '../../assets/icons/kyverno.svg';
import trivyIcon from '../../assets/icons/trivy.svg';

function useIsDark() {
  const { theme } = useTheme();
  return theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

type TriggerStyle = 'k8s' | 'cloud' | 'gcp' | 'request' | 'pagerduty' | 'posthog' | 'vercel' | 'railway' | 'flyio' | 'nebius' | 'beam' | 'daytona' | 'supabase' | 'planetscale' | 'neon' | 'clickhouse' | 'jenkins' | 'circleci' | 'sonarcloud' | 'okta' | 'databricks' | 'kyverno' | 'trivy' | 'terraform' | 'pulumi' | 'vault' | 'infisical' | 'github' | 'custom_webhook';

function getTriggerStyle(data: any): TriggerStyle {
  if (data.source === 'aws') return 'cloud';
  if (data.source === 'pagerduty') return 'pagerduty';
  if (data.source === 'posthog') return 'posthog';
  if (data.source === 'vercel') return 'vercel';
  if (data.source === 'railway') return 'railway';
  if (data.source === 'flyio') return 'flyio';
  if (data.source === 'nebius') return 'nebius';
  if (data.source === 'beam') return 'beam';
  if (data.source === 'daytona') return 'daytona';
  if (data.source === 'supabase') return 'supabase';
  if (data.source === 'planetscale') return 'planetscale';
  if (data.source === 'neon') return 'neon';
  if (data.source === 'clickhouse') return 'clickhouse';
  if (data.source === 'jenkins') return 'jenkins';
  if (data.source === 'circleci') return 'circleci';
  if (data.source === 'sonarcloud') return 'sonarcloud';
  if (data.source === 'okta') return 'okta';
  if (data.source === 'databricks') return 'databricks';
  if (data.source === 'gcp') return 'gcp';
  if (data.source === 'kyverno') return 'kyverno';
  if (data.source === 'trivy') return 'trivy';
  if (data.source === 'terraform') return 'terraform';
  if (data.source === 'pulumi') return 'pulumi';
  if (data.source === 'vault') return 'vault';
  if (data.source === 'infisical') return 'infisical';
  if (data.source === 'github') return 'github';
  if (data.source === 'custom_webhook') return 'custom_webhook';
  if (data.source === 'request') {
    const st = data.signal_type || '';
    if (st.includes('cloud')) return 'cloud';
    if (st.includes('k8s') || st.includes('kubernetes')) return 'k8s';
    return 'request';
  }
  return 'k8s';
}

function VerticalTriggerNode({ data, selected }: any) {
  const isDark = useIsDark();
  const style = getTriggerStyle(data);

  const colors = {
    border: selected ? 'border-primary shadow-lg ring-2 ring-primary/20' : 'border-zinc-300 dark:border-zinc-700',
    bg: 'bg-zinc-50 dark:bg-zinc-900/40',
    iconBg: 'bg-zinc-100 dark:bg-zinc-800',
    label: 'text-zinc-500 dark:text-zinc-400',
    handle: '!bg-zinc-400',
  };

  return (
    <div className="relative">
      <Handle type="target" position={Position.Top} className={`${colors.handle} !w-2 !h-2`} />
      <Handle type="source" position={Position.Bottom} className={`${colors.handle} !w-2 !h-2`} />
      <Card className={`w-[260px] p-3 border-2 transition-shadow ${colors.border} ${colors.bg}`}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${colors.iconBg}`}>
            {style === 'cloud' ? (
              <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />
            ) : style === 'request' ? (
              <img src={slackColorIcon} alt="Request" className="w-4 h-4" />
            ) : style === 'pagerduty' ? (
              <img src={pagerdutyIcon} alt="PagerDuty" className="w-4 h-4" />
            ) : style === 'posthog' ? (
              <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className="w-4 h-4 object-contain" />
            ) : style === 'vercel' ? (
              <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />
            ) : style === 'railway' ? (
              <img src={railwayIcon} alt="Railway" className="w-4 h-4 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />
            ) : style === 'flyio' ? (
              <img src={flyioIcon} alt="Fly.io" className="w-4 h-4 object-contain" />
            ) : style === 'nebius' ? (
              <img src={nebiusIcon} alt="Nebius" className="w-4 h-4 object-contain" />
            ) : style === 'beam' ? (
              <BeamIcon className="w-4 h-4 text-foreground" />
            ) : style === 'daytona' ? (
              <DaytonaIcon className="w-4 h-4 text-foreground" />
            ) : style === 'supabase' ? (
              <img src={supabaseIcon} alt="Supabase" className="w-4 h-4 object-contain" />
            ) : style === 'planetscale' ? (
              <PlanetScaleIcon className="w-4 h-4 text-foreground" />
            ) : style === 'neon' ? (
              <img src={neonIcon} alt="Neon" className="w-4 h-4 object-contain" />
            ) : style === 'clickhouse' ? (
              <img src={clickhouseIcon} alt="ClickHouse" className="w-4 h-4 object-contain" />
            ) : style === 'jenkins' ? (
              <img src={jenkinsIcon} alt="Jenkins" className="w-4 h-4 object-contain" />
            ) : style === 'circleci' ? (
              <CircleCIIcon className="w-4 h-4 text-foreground" />
            ) : style === 'sonarcloud' ? (
              <SonarCloudIcon className="w-4 h-4 text-foreground" />
            ) : style === 'okta' ? (
              <OktaIcon className="w-4 h-4 text-foreground" />
            ) : style === 'databricks' ? (
              <img src={databricksIcon} alt="Databricks" className="w-4 h-4 object-contain" />
            ) : style === 'gcp' ? (
              <GCPPreviewIcon className="w-4 h-4" />
            ) : style === 'kyverno' ? (
              <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4" />
            ) : style === 'trivy' ? (
              <img src={trivyIcon} alt="Trivy" className="w-4 h-4" />
            ) : style === 'terraform' ? (
              <img src={terraformIcon} alt="Terraform" className="w-4 h-4 object-contain" />
            ) : style === 'pulumi' ? (
              <img src={pulumiIcon} alt="Pulumi" className="w-4 h-4 object-contain" />
            ) : style === 'vault' ? (
              <img src={vaultIcon} alt="Vault" className="w-4 h-4 object-contain" />
            ) : style === 'infisical' ? (
              <img src={infisicalIcon} alt="Infisical" className="w-4 h-4 object-contain rounded-sm" />
            ) : style === 'github' ? (
              <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />
            ) : style === 'custom_webhook' ? (
              <Puzzle className={`w-4 h-4 ${colors.label}`} />
            ) : (
              <ShipWheel className="w-4 h-4 text-blue-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-medium uppercase tracking-wide ${colors.label}`}>Trigger</div>
            <div className="text-sm font-semibold truncate">{data.label || 'Signal'}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// sizeOnly drops text-colour utilities, keeping sizing. Brand icons that render
// in their own colour need this because the caller passes a muted text colour for
// the monochrome fallback icons.
function sizeOnly(cls: string): string {
  return cls.split(' ').filter(c => !/^(text-|dark:text-)/.test(c)).join(' ');
}

function IntegrationIcon({ integration, className }: { integration: string; className?: string }) {
  const isDark = useIsDark();
  const cls = className || 'w-4 h-4';

  switch (integration) {
    case 'kestrel':
      return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className={cls} />;
    case 'github':
      return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className={cls} />;
    case 'gitlab':
      return <img src={gitlabIcon} alt="GitLab" className={cls} />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" className={cls} />;
    case 'pagerduty':
      return <img src={pagerdutyIcon} alt="PagerDuty" className={cls} />;
    case 'confluence':
      return <img src={confluenceIcon} alt="Confluence" className={cls} />;
    case 'jira':
      return <img src={jiraIcon} alt="Jira" className={cls} />;
    case 'linear':
      return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" className={cls} />;
    case 'datadog':
      return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className={cls} />;
    case 'aws':
    case 'aws-cost':
    case 'aws-ops':
    case 'aws-security':
    case 'aws-data':
      return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className={cls} />;
    case 'kubernetes':
      // The call site passes colors.text for the monochrome fallback icons, so
      // the inherited text colour has to be dropped or it competes with blue.
      return <ShipWheel className={`${sizeOnly(cls)} text-blue-500`} />;
    case 'argocd':
      return <img src={argocdIcon} alt="ArgoCD" className={cls} />;
    case 'argo-rollouts':
      return <img src={argocdIcon} alt="Argo Rollouts" className={cls} />;
    case 'fluxcd':
      return <img src={fluxcdIcon} alt="Flux CD" className={cls} />;
    case 'karpenter':
      return <img src={karpenterIcon} alt="Karpenter" className={cls} />;
    case 'kyverno':
      return <img src={kyvernoIcon} alt="Kyverno" className={cls} />;
    case 'trivy':
      return <img src={trivyIcon} alt="Trivy" className={cls} />;
    case 'posthog':
      return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className={`${cls} object-contain`} />;
    case 'vercel':
      return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className={cls} />;
    case 'railway':
      return <img src={railwayIcon} alt="Railway" className={`${cls} object-contain`} style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
    case 'flyio':
      return <img src={flyioIcon} alt="Fly.io" className={`${cls} object-contain`} />;
    case 'nebius':
      return <img src={nebiusIcon} alt="Nebius" className={`${cls} object-contain`} />;
    case 'beam':
      return <BeamIcon className={`${cls} text-foreground`} />;
    case 'daytona':
      return <DaytonaIcon className={`${cls} text-foreground`} />;
    case 'supabase':
      return <img src={supabaseIcon} alt="Supabase" className={`${cls} object-contain`} />;
    case 'planetscale':
      return <PlanetScaleIcon className={`${cls} text-foreground`} />;
    case 'neon':
      return <img src={neonIcon} alt="Neon" className={`${cls} object-contain`} />;
    case 'clickhouse':
      return <img src={clickhouseIcon} alt="ClickHouse" className={`${cls} object-contain`} />;
    case 'jenkins':
      return <img src={jenkinsIcon} alt="Jenkins" className={`${cls} object-contain`} />;
    case 'circleci':
      return <CircleCIIcon className={`${cls} text-foreground`} />;
    case 'sonarcloud':
      return <SonarCloudIcon className={`${cls} text-foreground`} />;
    case 'okta':
      return <OktaIcon className={`${cls} text-foreground`} />;
    case 'databricks':
      return <img src={databricksIcon} alt="Databricks" className={`${cls} object-contain`} />;
    case 'gcp-cost':
    case 'gcp':
    case 'gcp-security':
    case 'gcp-data':
      return <GCPPreviewIcon className={`${cls} object-contain`} />;
    case 'terraform':
      return <img src={terraformIcon} alt="Terraform" className={`${cls} object-contain`} />;
    case 'pulumi':
      return <img src={pulumiIcon} alt="Pulumi" className={`${cls} object-contain`} />;
    case 'vault':
      return <img src={vaultIcon} alt="Vault" className={`${cls} object-contain`} />;
    case 'infisical':
      return <img src={infisicalIcon} alt="Infisical" className={`${cls} object-contain rounded-sm`} />;
    case 'approval':
      return <ShieldCheck className={cls} />;
    case 'condition':
      return <GitFork className={cls} />;
    case 'notion':
      return <BookOpen className={cls} />;
    default:
      return <Code className={cls} />;
  }
}

function VerticalActionNode({ data, selected }: any) {
  const integration = data.integration || 'kestrel';
  const colors = {
    bg: 'bg-zinc-50 dark:bg-zinc-900/40',
    border: 'border-zinc-300 dark:border-zinc-700',
    text: 'text-zinc-500 dark:text-zinc-400',
    accent: 'bg-zinc-100 dark:bg-zinc-800',
  };

  const integrationLabel = formatInternalName(integration);

  return (
    <div className="relative">
      <Handle type="target" position={Position.Top} className="!bg-primary !w-2 !h-2" />
      <Handle type="source" position={Position.Bottom} className="!bg-primary !w-2 !h-2" />
      <Card
        className={`w-[260px] p-3 border-2 transition-shadow ${
          selected ? 'shadow-lg ring-2 ring-primary/30 border-primary' : colors.border
        } ${colors.bg}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${colors.accent}`}>
            <IntegrationIcon integration={integration} className={`w-4 h-4 ${colors.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-medium uppercase tracking-wide ${colors.text}`}>
              {integrationLabel}
            </div>
            <div className="text-sm font-semibold truncate">{data.label || data.action || 'Action'}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

const nodeTypes = { trigger: VerticalTriggerNode, action: VerticalActionNode };

interface WorkflowPreviewProps {
  workflow: {
    name?: string;
    description?: string;
    definition?: { nodes?: any[]; edges?: any[] };
    trigger_config?: any;
    trigger?: { source?: string; signal_type?: string; description?: string };
    actions?: Array<{ action_id?: string; label?: string; integration?: string; on_true?: Array<{ action_id?: string; label?: string; integration?: string }>; on_false?: Array<{ action_id?: string; label?: string; integration?: string }> }>;
  };
  onSave?: (workflow: any) => Promise<boolean | void> | boolean | void;
  onCreate?: (workflow: any) => void;
  onEdit?: (workflow: any) => void;
}

function mapActionToIntegration(actionId: string): string {
  if (actionId.startsWith('slack')) return 'slack';
  if (actionId.startsWith('jira')) return 'jira';
  if (actionId.startsWith('linear')) return 'linear';
  if (actionId.startsWith('github')) return 'github';
  if (actionId.startsWith('gitlab')) return 'gitlab';
  if (actionId.startsWith('confluence')) return 'confluence';
  if (actionId.startsWith('rollouts')) return 'argo-rollouts';
  if (actionId.startsWith('flux')) return 'fluxcd';
  if (actionId.startsWith('karpenter')) return 'karpenter';
  if (actionId.startsWith('kyverno')) return 'kyverno';
  if (actionId.startsWith('trivy')) return 'trivy';
  if (actionId.startsWith('argocd')) return 'argocd';
  if (actionId.startsWith('pagerduty') || actionId.startsWith('pd-')) return 'pagerduty';
  if (actionId.startsWith('vercel')) return 'vercel';
  if (actionId.startsWith('railway')) return 'railway';
  if (actionId.startsWith('flyio')) return 'flyio';
  if (actionId.startsWith('nebius')) return 'nebius';
  if (actionId.startsWith('posthog')) return 'posthog';
  if (actionId.startsWith('datadog') || actionId.startsWith('dd-')) return 'datadog';
  if (actionId.startsWith('aws') || actionId.startsWith('cloud-cost')) return 'aws-cost';
  if (actionId.startsWith('approval')) return 'approval';
  if (actionId.startsWith('kubernetes') || actionId.startsWith('k8s') || actionId.startsWith('kubectl')) return 'kubernetes';
  if (actionId.startsWith('condition') || actionId.startsWith('check-')) return 'kestrel';
  return 'kestrel';
}

export function WorkflowPreview({ workflow, onSave, onCreate, onEdit }: WorkflowPreviewProps) {
  const isDark = useIsDark();
  const [sdkCode, setSdkCode] = useState('');
  const [sdkLoading, setSdkLoading] = useState(false);
  const [sdkCopied, setSdkCopied] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const handleGenerateSDK = () => {
    if (sdkCode) {
      setSdkCode('');
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSdkCode('');
    setSdkLoading(true);

    apiClient.streamSDKCode(
      {
        workflow_name: workflow.name || 'Untitled Workflow',
        description: workflow.description || '',
        definition: workflow.definition || { nodes: [], edges: [] },
        trigger_config: workflow.trigger_config || null,
      },
      {
        onChunk: (chunk) => setSdkCode(prev => prev + chunk),
        onDone: () => setSdkLoading(false),
        onError: () => setSdkLoading(false),
      },
      controller.signal,
    );
  };

  const handleCopySDK = async () => {
    await navigator.clipboard.writeText(sdkCode);
    setSdkCopied(true);
    setTimeout(() => setSdkCopied(false), 2000);
  };

  const { nodes, edges } = useMemo(() => {
    // Builder format: definition.nodes + definition.edges (already has positions and proper branching)
    if (workflow.definition?.nodes?.length) {
      const rawNodes = workflow.definition.nodes;
      const rawEdges = workflow.definition.edges || [];

      // Use dagre for automatic graph layout (handles any branching factor, convergence, etc.)
      const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
      g.setGraph({ rankdir: 'TB', nodesep: 180, ranksep: 80, marginx: 10, marginy: 10, ranker: 'tight-tree' });

      rawNodes.forEach((node: any) => {
        g.setNode(node.id, { width: 280, height: 70 });
      });
      rawEdges.forEach((edge: any) => {
        g.setEdge(edge.source, edge.target);
      });

      Dagre.layout(g);

      const n = rawNodes.map((node: any) => {
        const pos = g.node(node.id);
        const nodeType = node.type === 'trigger' ? 'trigger' :
                         node.type === 'condition' ? 'action' :
                         node.type === 'approval' ? 'action' : 'action';
        const data = { ...node.data };
        if (node.type === 'condition') {
          data.integration = 'condition';
        } else if (node.type === 'approval') {
          data.integration = 'approval';
        } else if (node.type === 'action' && !data.integration) {
          data.integration = mapActionToIntegration(data.action || '');
        }
        if (node.type === 'trigger') {
          data.source = data.source || 'kubernetes';
        }
        return { id: node.id, type: nodeType, position: { x: pos.x - 140, y: pos.y - 35 }, data };
      });

      const e = rawEdges.map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: 'smoothstep',
        label: edge.label || undefined,
      }));

      return { nodes: n, edges: e };
    }

    // Legacy simplified format: trigger + actions array
    const n: any[] = [];
    const e: any[] = [];
    const yStep = 100;
    const xCenter = 140;
    const branchOffset = 160;
    let y = 0;

    if (workflow.trigger) {
      n.push({
        id: 'trigger',
        type: 'trigger',
        position: { x: xCenter, y },
        data: {
          label: workflow.trigger.description || workflow.trigger.signal_type || workflow.trigger.source || 'Trigger',
          source: workflow.trigger.source || 'kubernetes',
          signal_type: workflow.trigger.signal_type || 'any',
        },
      });
      y += yStep;
    }

    let prevId = 'trigger';

    const actions = workflow.actions || [];
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];

      if (action.action_id === 'condition' || action.on_true || action.on_false) {
        const condId = `condition-${i}`;
        n.push({
          id: condId,
          type: 'action',
          position: { x: xCenter, y },
          data: {
            label: action.label || 'Condition',
            integration: action.integration || mapActionToIntegration(action.action_id || ''),
          },
        });
        e.push({ id: `e-${prevId}-${condId}`, source: prevId, target: condId, type: 'smoothstep' });
        y += yStep;

        const trueBranch = action.on_true || [];
        let truePrevId = condId;
        for (let j = 0; j < trueBranch.length; j++) {
          const tNodeId = `true-${i}-${j}`;
          n.push({
            id: tNodeId,
            type: 'action',
            position: { x: xCenter - branchOffset, y },
            data: {
              label: trueBranch[j].label || trueBranch[j].action_id || 'Step',
              integration: trueBranch[j].integration || mapActionToIntegration(trueBranch[j].action_id || ''),
            },
          });
          e.push({
            id: `e-${truePrevId}-${tNodeId}`,
            source: truePrevId,
            target: tNodeId,
            type: 'smoothstep',
            label: j === 0 ? 'true' : undefined,
          });
          truePrevId = tNodeId;
          y += yStep;
        }

        const falseBranch = action.on_false || [];
        let falsePrevId = condId;
        let falseY = n.find(nd => nd.id === condId)!.position.y + yStep;
        for (let j = 0; j < falseBranch.length; j++) {
          const fNodeId = `false-${i}-${j}`;
          n.push({
            id: fNodeId,
            type: 'action',
            position: { x: xCenter + branchOffset, y: falseY },
            data: {
              label: falseBranch[j].label || falseBranch[j].action_id || 'Step',
              integration: falseBranch[j].integration || mapActionToIntegration(falseBranch[j].action_id || ''),
            },
          });
          e.push({
            id: `e-${falsePrevId}-${fNodeId}`,
            source: falsePrevId,
            target: fNodeId,
            type: 'smoothstep',
            label: j === 0 ? 'false' : undefined,
          });
          falsePrevId = fNodeId;
          falseY += yStep;
        }

        y = Math.max(y, falseY);
        prevId = truePrevId;
      } else {
        const nodeId = `action-${i}`;
        n.push({
          id: nodeId,
          type: 'action',
          position: { x: xCenter, y },
          data: {
            label: action.label || action.action_id || `Step ${i + 1}`,
            integration: action.integration || mapActionToIntegration(action.action_id || ''),
          },
        });
        e.push({ id: `e-${prevId}-${nodeId}`, source: prevId, target: nodeId, type: 'smoothstep' });
        prevId = nodeId;
        y += yStep;
      }
    }

    return { nodes: n, edges: e };
  }, [workflow]);

  const maxY = nodes.reduce((max, n) => Math.max(max, n.position.y), 0);
  const minY = nodes.reduce((min, n) => Math.min(min, n.position.y), Infinity);
  const maxX = nodes.reduce((max, n) => Math.max(max, n.position.x), 0);
  const minX = nodes.reduce((min, n) => Math.min(min, n.position.x), Infinity);
  const graphWidth = (maxX - minX) + 280;
  const graphSpanY = (maxY - minY) + 80;
  // Container height = graph height scaled to fit container width, ensuring all nodes visible
  const estimatedContainerWidth = 480;
  const scale = Math.min(1, estimatedContainerWidth / Math.max(graphWidth, 1));
  const fitScale = scale * 0.88; // Allow fitView slightly more room than minZoom
  const graphHeight = Math.max(200, Math.round(graphSpanY * scale) + 20);

  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-muted/20">
        <span className="text-sm font-medium">{workflow.name || 'Workflow'}</span>
        <button
          onClick={handleGenerateSDK}
          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 whitespace-nowrap shrink-0"
        >
          {sdkLoading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Code className="w-2.5 h-2.5" />}
          {sdkCode ? 'Hide' : 'SDK Code'}
        </button>
      </div>
      {workflow.description && (
        <p className="text-xs text-muted-foreground px-3 pt-2">{workflow.description}</p>
      )}
      {nodes.length > 0 && (
      <div style={{ height: graphHeight }} className="w-full cursor-grab active:cursor-grabbing">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.05, maxZoom: 1 }}
          zoomOnScroll={true}
          zoomOnPinch={true}
          zoomOnDoubleClick={false}
          panOnDrag={true}
          panOnScroll={false}
          preventScrolling={false}
          minZoom={fitScale}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        </ReactFlow>
      </div>
      )}
      {sdkCode && (
        <div className="border-t border-border/50">
          <div className="flex items-center justify-between px-3 py-1.5 bg-muted/30">
            <span className="text-[10px] text-muted-foreground font-medium">Python SDK</span>
            <button
              onClick={handleCopySDK}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {sdkCopied ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
              {sdkCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {/* Same Prism setup as the builder's Python SDK code panel, sized
              for the in-chat card. */}
          <div className="chat-sdk-code-wrapper max-h-[250px] overflow-y-auto">
            <style>{`
              .chat-sdk-code-wrapper pre {
                margin: 0 !important; padding: 8px 12px !important;
                border-radius: 0 !important; border: none !important;
                overflow-x: hidden !important; white-space: pre-wrap !important;
                word-break: break-word !important; font-size: 11px !important;
                line-height: 1.6 !important;
                background: hsl(var(--background)) !important;
              }
              .chat-sdk-code-wrapper pre code { white-space: pre-wrap !important; word-break: break-word !important; overflow-wrap: break-word !important; background: transparent !important; }
              .chat-sdk-code-wrapper pre code span { white-space: pre-wrap !important; background: transparent !important; }
            `}</style>
            <SyntaxHighlighter language="python" style={isDark ? oneDark : oneLight}>
              {sdkCode}
            </SyntaxHighlighter>
          </div>
        </div>
      )}
      {(onSave || onCreate || onEdit) ? (
        <div className="flex gap-2 px-3 py-2 border-t border-border/50">
          {onSave && (
            <SaveToLibraryButton
              onSave={() => onSave(workflow)}
              className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors"
            />
          )}
          {onCreate && (
            <button
              onClick={() => onCreate(workflow)}
              className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Create as Draft
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => onEdit(workflow)}
              className="px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Edit Workflow
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
